import React, { useState, useRef, useEffect } from "react"
import { ManageLogData } from "./ManageLogTile"
import Paginator from "../../Paginator"
import { getManageLifeLogList } from "../../../api/manage_lifelog_list"
import { useNavigate } from "react-router-dom"
import { useLoading } from "../../LoadingContext"
import DashboardLayout from "../../components/DashboardLayout"
import DashboardNavbar from "../../components/DashboardNavbar"
import MDBox from "components/MDBox"
import MDTypography from "components/MDTypography"
import MDButton from "components/MDButton"
import Icon from "@mui/material/Icon"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import Card from "@mui/material/Card"
import MDInput from "components/MDInput"

// プルダウン関連
// サービス提供元
import { getManageCatvList } from "../../../api/manage_catv_list"
import { ManageCatvData } from "../catv"
// 不動産業者
import { getManageEstateList } from "../../../api/manage_estate_list"
import { ManageEstateData } from "../estate"
// 施設
import { getFacilityList } from "../../../api/facility_list"
import { ManageFacilityData } from "../facility"
// 入居者
import { getMedicalRecordList } from "../../../api/medical_record_list"
interface MedicalRecordData {
  id: string
  room_no: number | null
}

import { useConnectionError } from "../../ConnectionErrorContext"

const ManageLogTileList: React.FC = () => {
  const navigate = useNavigate()
  const { startLoading, stopLoading } = useLoading()
  const { showConnectionError } = useConnectionError()

  // ログデータ
  const [logData, setLogData] = useState<ManageLogData[]>([])
  const [filteredData, setFilteredData] = useState<ManageLogData[]>([])

  // ページャー関連
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 50

  // プルダウン関連
  // サービス提供元
  const [catvData, setCatvData] = useState<ManageCatvData[]>([])
  const [selectedCatv, setSelectedCatv] = useState<string>("")
  // 不動産業者
  const [estateData, setEstateData] = useState<ManageEstateData[]>([])
  const [filteredModeEstate, setFilteredModeEstate] = useState<ManageEstateData[]>([])
  const [selectedEstate, setSelectedEstate] = useState<string>("")
  // 施設
  const [facilityData, setFacilityData] = useState<ManageFacilityData[]>([])
  const [filteredModeFacility, setFilteredModeFacility] = useState<ManageFacilityData[]>([])
  const [selectedFacility, setSelectedFacility] = useState<string>("")
  // 入居者
  const [medicalRecordData, setMedicalRecordData] = useState<MedicalRecordData[]>([])
  const [selectedMedicalRecord, setSelectedMedicalRecord] = useState<string>("")

  // 検索日時関連
  const [date, setDate] = useState("")
  const [startTime, setStartTime] = useState("")
  const [endTime, setEndTime] = useState("")

  // 検索
  const [searchVisible, setSearchVisible] = useState(false)
  const tableContainerRef = useRef<HTMLDivElement | null>(null)

  // 不正アクセス時の処理
  if (localStorage.getItem("session_code") == "") {
    navigate("/login")
    return <></>
  }
  const auth = localStorage.getItem("account_type")
  if (auth != "service") {
    navigate("/") // 不正アクセス
  }

  // 初期表示時に初期ページを表示
  useEffect(() => {
    setFilteredData(logData)
  }, [logData])

  // 日時表示変換
  const formatDateWithLocale = (timestamp: number) => {
    return new Intl.DateTimeFormat("ja-JP", {
      dateStyle: "medium",
      timeStyle: "short"
    }).format(new Date(timestamp * 1000)) // 秒をミリ秒に変換
  }

  // プルダウン関連
  // サービス提供元、不動産業者、施設一覧を取得
  const fetchManageCatvList = async () => {
    try {
      startLoading()

      // サービス提供元一覧
      const manageCatvData = await getManageCatvList()
      setCatvData(manageCatvData.catv_list)

      // 不動産一覧
      const manageEstateData = await getManageEstateList(1)
      setEstateData(manageEstateData.estate_list)

      // 施設一覧
      const manageFacilityData = await getFacilityList(1)
      setFacilityData(manageFacilityData.facility_list)
    } catch (error: any) {
      if (error.response && error.response.status === 401) {
        navigate("/login")
      } else if (!error.response) {
        showConnectionError(true)
      }
    } finally {
      stopLoading()
    }
  }
  useEffect(() => {
    fetchManageCatvList()
  }, [])

  // サービス提供元選択
  const handleSelectedChangeCatv = (e: any) => {
    // 選択中のサービス提供元を設定
    setSelectedCatv(e.target.value)
    // サービス提供元に応じて不動産業者をフィルタリング
    const filteredEstate = estateData.filter((estate) => `Catv|${estate.catv_id}` === e.target.value)
    setFilteredModeEstate(filteredEstate)
    setSelectedEstate("-")
    // 施設は空にする
    setFilteredModeFacility([])
    setSelectedFacility("-")
    setSearchVisible(false)
    // 入居者は空にする
    setSelectedMedicalRecord("-")
  }

  // 不動産業者選択
  const handleSelectedChangeEstate = (e: any) => {
    // 選択中の不動産業者を設定
    setSelectedEstate(e.target.value)
    // 不動産業者に応じて不動産業者をフィルタリング
    const filteredFacility = facilityData.filter((facility) => `Estate|${facility.estate_id}` === e.target.value)
    setFilteredModeFacility(filteredFacility)
    setSelectedFacility("-")
    setSearchVisible(false)
    // 入居者は空にする
    setSelectedMedicalRecord("-")
  }

  // 施設選択
  const handleSelectedChangeFacility = async (e: any) => {
    // 選択中の施設を設定
    setSelectedFacility(e.target.value)

    if (e.target.value === "-") {
      setMedicalRecordData([])
      setSearchVisible(false)
      setSelectedMedicalRecord("-")
      return
    }

    // 入居者情報は施設を選択したタイミングで取得
    try {
      startLoading()
      if (e.target.value) {
        const facility_id = e.target.value.split("|")[1]
        const medicalRecordData = await getMedicalRecordList(facility_id, 0)

        // room_no 昇順でソート
        const sortedList = (medicalRecordData.medical_record_list || []).sort((a: any, b: any) => a.room_no - b.room_no)

        // state にセット
        setMedicalRecordData(sortedList)
      }
      setSearchVisible(true)
    } catch (error: any) {
      if (error.response && error.response.status === 401) {
        navigate("/login")
      } else if (!error.response) {
        showConnectionError(true)
      }
    } finally {
      stopLoading()
    }
    setSelectedMedicalRecord("-")
  }

  // 入居者選択
  const handleSelectedChangeMedicalRecord = async (e: any) => {
    // 選択中の施設を設定
    setSelectedMedicalRecord(e.target.value)
  }

  // Date → ISO文字列に変換（秒を59にする場合は isEnd=true）
  const buildDateTime = (dateObj: Date, isEnd = false) => {
    const yyyy = dateObj.getFullYear()
    const mm = String(dateObj.getMonth() + 1).padStart(2, "0")
    const dd = String(dateObj.getDate()).padStart(2, "0")
    const hh = String(dateObj.getHours()).padStart(2, "0")
    const min = String(dateObj.getMinutes()).padStart(2, "0")
    const ss = isEnd ? "59" : String(dateObj.getSeconds()).padStart(2, "0")

    return `${yyyy}-${mm}-${dd}T${hh}:${min}:${ss}`
  }

  // 検索ボタン押下時処理
  const handleSearch = () => {
    const now = new Date()

    // date が未入力 → 今日の日付
    const targetDate = date || now.toISOString().slice(0, 10) // "YYYY-MM-DD"

    // まず Date オブジェクトを作る
    let startDate: Date
    let endDate: Date

    if (!startTime && !endTime) {
      if (date) {
        startDate = new Date(`${targetDate}T00:00:00`)
        endDate = new Date(`${targetDate}T23:59:00`)
      } else {
        startDate = new Date(now.getTime() - 60 * 60 * 1000)
        endDate = now
      }
    } else if (!startTime && endTime) {
      endDate = new Date(`${targetDate}T${endTime}:59`)
      startDate = new Date(endDate.getTime() - 60 * 60 * 1000)
    } else if (startTime && !endTime) {
      startDate = new Date(`${targetDate}T${startTime}:00`)
      endDate = new Date(startDate.getTime() + 60 * 60 * 1000)
    } else {
      startDate = new Date(`${targetDate}T${startTime}:00`)
      endDate = new Date(`${targetDate}T${endTime}:59`)
    }

    // 開始時間 > 終了時間の場合は入れ替え
    if (startDate > endDate) {
      const tmp = startDate
      startDate = endDate
      endDate = tmp
    }

    // buildDateTime を呼ぶのは入れ替え後
    const start = buildDateTime(startDate, false)
    const end = buildDateTime(endDate, true)

    console.log("検索範囲:", start, "～", end)
    fetchManageLogList(null, [], start, end)
  }

  // ページャー関連
  const totalPages = Math.ceil(filteredData.length / itemsPerPage)
  const paginatedData = filteredData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
  useEffect(() => {
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollTop = 0
    }
  }, [currentPage, logData])

  // ライフログ検索
  const fetchManageLogList = async (
    startKey: any = null,
    accumulatedData: any[] = [],
    searchStartDate?: string,
    searchEndDate?: string
  ) => {
    try {
      if (startKey === null) {
        startLoading()
      }

      let medical_record_id = ""
      if (selectedMedicalRecord !== "-") {
        medical_record_id = selectedMedicalRecord.split("|")[1]
      }

      const manageLogData = await getManageLifeLogList(
        formatDate(searchStartDate || ""),
        formatDate(searchEndDate || ""),
        selectedFacility.split("|")[1],
        medical_record_id
      )

      const newData = manageLogData.lifelog_list || []
      const combinedData = [...accumulatedData, ...newData]
      const sortedData = combinedData.sort((a: any, b: any) => b.create_date - a.create_date)
      setLogData(sortedData)
      setFilteredData(sortedData)
      setCurrentPage(1)
      stopLoading()
    } catch (error: any) {
      stopLoading()
      if (error.message === "session") {
        navigate("/")
      } else if (!error.response) {
        showConnectionError(true)
      }
    }
  }

  // 日付のフォーマット
  const formatDate = (isoString: string) => {
    const date = new Date(isoString)
    const yyyy = date.getFullYear()
    const mm = String(date.getMonth() + 1).padStart(2, "0") // 月を2桁に
    const dd = String(date.getDate()).padStart(2, "0") // 日を2桁に
    const hh = String(date.getHours()).padStart(2, "0") // 時を2桁に
    const min = String(date.getMinutes()).padStart(2, "0") // 分を2桁に
    const ss = String(date.getSeconds()).padStart(2, "0") // 秒を2桁に

    return `${yyyy}/${mm}/${dd} ${hh}:${min}:${ss}`
  }

  // CSVダウンロード処理
  const handleDownloadCSV = () => {
    // infoが存在する場合にCSVヘッダーを作成
    const headers = [
      "日付時刻",
      "ログ種別",
      "ログ内容（機器エラー、通知）",
      "施設ID",
      "施設名",
      "施設住所",
      "施設電話番号",
      "居室番号",
      "入居書イニシャル"
    ]

    const escapeCSV = (value: any) => {
      if (value === null || value === undefined) {
        return "" // null または undefined は空文字列に変換
      }
      const stringValue = String(value)
      return `"${stringValue.replace(/"/g, '""')}"` // ダブルクォートをエスケープ
    }

    // CSVデータの生成
    const csvContent = [
      headers.join(","), // ヘッダー行
      ...logData.map((item) => {
        const formattedDate = formatDateWithLocale(item.create_date)
        const log_type = item.log_type == 0 ? "不在検知ログ" : "機器エラーログ"

        const log = JSON.stringify(item?.log) || ""
        const facilityId = item?.facility_id || ""
        const facilityName = item?.facility_name || ""
        const facilityAddress = item?.facility_address || ""
        const facilityPhone = item?.facility_phone || ""
        const roomNo = item?.room_no || 0
        const initial = item?.initial || ""

        // データ行を生成
        return [
          formattedDate,
          log_type,
          escapeCSV(log),
          facilityId,
          facilityName,
          facilityAddress,
          facilityPhone,
          roomNo,
          initial
        ].join(",")
      })
    ].join("\n")

    // 文字化け防止のためにBOMを追加
    const utf8Bom = "\uFEFF"
    const blob = new Blob([utf8Bom + csvContent], { type: "text/csvcharset=utf-8" })

    // ファイルをダウンロード
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = "life_log.csv"
    link.click()
    URL.revokeObjectURL(url)
  }

  return (
    <DashboardLayout>
      <MDBox mb={4}>
        <DashboardNavbar absolute={true} />
      </MDBox>
      <MDBox display="flex" justifyContent="space-between" alignItems="center" pt={3} pb={3}>
        <MDBox>
          <MDTypography variant="h4" gutterBottom>
            ライフログ
          </MDTypography>
        </MDBox>
      </MDBox>

      <Card
        sx={{
          width: "100%",
          maxHeight: "calc(100vh - 100px)",
          boxSizing: "border-box"
        }}
      >
        {/* 検索フォーム */}
        <MDBox display="flex" flexWrap="wrap" mb={2} gap={2} pl={2} pr={2} pt={2}>
          <select name="selectedCatv" value={selectedCatv} onChange={handleSelectedChangeCatv}>
            <option key={0} value={"-"}>
              -
            </option>
            {catvData.map((row) => (
              <option key={row.id} value={row.id}>
                {row.name}
              </option>
            ))}
          </select>
          <select name="selectedEstate" value={selectedEstate} onChange={handleSelectedChangeEstate}>
            <option key={0} value={"-"}>
              -
            </option>
            {filteredModeEstate.map((row) => (
              <option key={row.id} value={row.id}>
                {row.name}
              </option>
            ))}
          </select>
          <select name="selectedFacility" value={selectedFacility} onChange={handleSelectedChangeFacility}>
            <option key={0} value={"-"}>
              -
            </option>
            {filteredModeFacility.map((row) => (
              <option key={row.id} value={row.id}>
                {row.name}
              </option>
            ))}
          </select>
          <select
            name="selectedMedicalRecord"
            value={selectedMedicalRecord}
            onChange={handleSelectedChangeMedicalRecord}
          >
            <option key={0} value={"-"}>
              -
            </option>
            {medicalRecordData.map((row) => (
              <option key={row.id} value={row.id}>
                {row.room_no}
              </option>
            ))}
          </select>
        </MDBox>
        <MDBox display="flex" flexWrap="wrap" gap={2} mb={4} pl={2} pr={2}>
          {/* 日付 */}
          <MDInput type="date" value={date} onChange={(e) => setDate(e.target.value)} sx={{ width: "160px" }} />
          {/* 開始時間 */}
          <MDInput
            type="time"
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
            sx={{ width: "160px" }}
          />
          <span style={{ paddingTop: "12px", fontSize: "0.75em" }}>〜</span>
          {/* 終了時間 */}
          <MDInput type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} sx={{ width: "160px" }} />
          <MDButton
            variant="gradient"
            color="info"
            size="medium"
            onClick={handleSearch}
            disabled={!searchVisible}
            sx={{ minWidth: "120px" }} // 最小幅を確保
          >
            <Icon>search</Icon>検索
          </MDButton>

          <MDButton
            variant="outlined"
            color="info"
            size="medium"
            onClick={handleDownloadCSV}
            sx={{ minWidth: "120px" }} // 最小幅を確保
          >
            <Icon>download</Icon>CSV
          </MDButton>
        </MDBox>

        {/* ログ一覧 */}
        <TableContainer
          ref={tableContainerRef}
          sx={{ overflowX: "auto", overflowY: "auto", maxHeight: "calc(100vh - 400px)", marginTop: "20px" }}
        >
          <Table stickyHeader>
            <TableHead sx={{ position: "sticky", display: "table-header-group", zIndex: 3 }}>
              <TableRow
                sx={{
                  "& .MuiTableCell-root": {
                    fontSize: "0.875rem",
                    color: "text.primary",
                    fontWeight: "bold",
                    backgroundColor: "#FFFFFF"
                  }
                }}
              >
                <TableCell align="left">日付時刻</TableCell>
                <TableCell align="left">ログ種別</TableCell>
                <TableCell align="left">ログ内容</TableCell>
                <TableCell align="left">施設住所</TableCell>
                <TableCell align="left">施設電話番号</TableCell>
                <TableCell align="left">居室番号</TableCell>
                <TableCell align="left">入居者イニシャル</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.map((row, index) => (
                <TableRow
                  key={index}
                  sx={{
                    "& .MuiTableCell-root": {
                      fontSize: "0.875rem",
                      color: "text.primary",
                      fontWeight: "normal",
                      zIndex: 0
                    }
                  }}
                >
                  <TableCell align="left">
                    <div style={{ minWidth: "120px" }}>{formatDateWithLocale(row.create_date)}</div>
                  </TableCell>
                  <TableCell align="left">
                    <div style={{ minWidth: "100px" }}>{row.log_type == 0 ? "不在検知" : "機器エラー"}</div>
                  </TableCell>
                  <TableCell align="left">
                    <div style={{ minWidth: "200px" }}>{row.log ? JSON.stringify(row.log, null, 2) : "None"}</div>
                  </TableCell>
                  <TableCell align="left">
                    <div style={{ minWidth: "100px" }}>{row.facility_address}</div>
                  </TableCell>
                  <TableCell align="left">
                    <div style={{ minWidth: "100px" }}>{row.facility_phone}</div>
                  </TableCell>
                  <TableCell align="left">
                    <div style={{ minWidth: "80px" }}>{row.room_no}</div>
                  </TableCell>
                  <TableCell align="left">
                    <div style={{ minWidth: "80px" }}>{row.initial}</div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
      {/* ページャー */}
      <Paginator
        key={"ManageFacilityTileListPaginator"}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />
    </DashboardLayout>
  )
}

export default ManageLogTileList
